import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { Box } from 'models/Box';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import { saveBox } from '../services/box.service';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { isContext } from 'utils/ContextUtils';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

type Context = {
  previousBoxes: Box[];
};

export type Variables = {
  box: Box;
};

export const useSaveBox = ({
  onSuccess,
}: {
  onSuccess?: (data: Box) => void;
} = {}): UseMutationResult<Box, Error, Variables, Context> => {
  const queryClient = useQueryClient();
  const { selectedSpace } = useActiveSpace();

  const onMutateCallback = async (variables: Variables) => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({
      queryKey: boxesQuery(selectedSpace?.id).queryKey,
    });

    // Snapshot the previous value
    const previousBoxes = queryClient.getQueryData<Box[]>(
      boxesQuery(selectedSpace?.id).queryKey,
    );

    // Whether the box already exists in the stack
    const itExists =
      previousBoxes instanceof Array &&
      previousBoxes.some((b) => b.id === variables.box.id);

    // Optimistically update to the new value
    if (itExists) {
      queryClient.setQueryData<Box[]>(
        boxesQuery(selectedSpace?.id).queryKey,
        previousBoxes.map((b) => {
          if (b.id !== variables.box.id) return b;
          // The form only sends the name and access level, so lay them over
          // the box we already have: its permissions, stats, settings,
          // members and look must survive until the refetch below replaces
          // all of this. Dropping `settings` here crashed the dashboard.
          return { ...b, ...variables.box };
        }),
      );
    } else {
      // We don't do anything because we don't have an ID to use as key
    }

    return { previousBoxes };
  };

  // We don't put the saved box in the cache: the API returns it without
  // stats. The list is refetched below instead.
  // This follows React Query's recommended optimistic update pattern.
  const onSuccessCallback = (box: Box, variables: Variables) => {
    // A box with an id was edited, one without an id was just created.
    notify(
      variables.box.id
        ? NotificationType.BOX_UPDATED
        : NotificationType.BOX_CREATED,
    );

    onSuccess?.(box);
  };

  // Whether the save worked or not, reload the boxes so the list matches the
  // server. The mutation stays pending until the reload is done.
  const onSettledCallback = () =>
    queryClient.invalidateQueries({
      queryKey: boxesQuery(selectedSpace?.id).queryKey,
    });

  const onErrorCallback = (
    _err: Error,
    _variables: Variables,
    context: Context | undefined,
  ) => {
    // If the mutation fails, use the context returned from onMutate to roll back
    if (isContext<Context>(context, 'previousBoxes')) {
      queryClient.setQueryData<Box[]>(
        boxesQuery(selectedSpace?.id).queryKey,
        context.previousBoxes,
      );
    }
  };

  const saveBoxMutation = useMutation<Box, Error, Variables, Context>({
    mutationFn: (variables) => saveBox(variables.box, selectedSpace),
    onMutate: onMutateCallback,
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
    onSettled: onSettledCallback,
  });

  return saveBoxMutation;
};
